/**
 * Sync Status Component (Web App Version)
 * 
 * Displays sync status, progress, and controls in the UI.
 * Shows online/offline status, last sync time, and sync state.
 * 
 * Simplified for web app - uses DatabaseService instead of Electron IPC.
 */

import { useState, useEffect } from 'react';
import databaseService from '../services/database.service';

export default function SyncStatus() {
  const [syncStatus, setSyncStatus] = useState({
    status: 'offline',
    lastSyncTime: null,
    error: null
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Subscribe to sync status changes from DatabaseService
  useEffect(() => {
    // Subscribe to sync status updates
    const unsubscribe = databaseService.onSyncStatusChange((status) => {
      setSyncStatus(status);
    });

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const isSyncActive = syncStatus.status === 'syncing';
  const hasError = syncStatus.status === 'error';

  // Simple colored dot indicator
  const getStatusColor = () => {
    if (!isOnline) return 'bg-gray-400';
    if (hasError) return 'bg-red-500';
    if (isSyncActive) return 'bg-yellow-500';
    if (syncStatus.status === 'synced') return 'bg-green-500';
    return 'bg-gray-400';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (hasError) return 'Sync error';
    if (isSyncActive) return 'Syncing...';
    if (syncStatus.status === 'synced') return 'Synced';
    return 'Offline';
  };

  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      {/* Status Dot */}
      <div 
        className={`w-2 h-2 rounded-full ${getStatusColor()} ${isSyncActive ? 'animate-pulse' : ''}`}
        title={getStatusText()}
      />
      
      {/* Status Text */}
      <span className="text-xs text-[var(--color-text-secondary)]">
        {getStatusText()}
      </span>

      {/* Last Sync Time (optional) */}
      {syncStatus.lastSyncTime && syncStatus.status === 'synced' && (
        <span className="text-xs text-[var(--color-text-muted)]">
          • {formatLastSync(syncStatus.lastSyncTime)}
        </span>
      )}
    </div>
  );
}

function formatLastSync(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
}
